import { get } from 'lodash'
import React, { useContext, useEffect, useMemo } from 'react'

import NumberInput from './number_input'
import { StoreContext } from '../providers/store_provider'
import { type Validator, validate } from '../utilities/validation_utils'

function _validate(
    value: number | string,
    valueValidators: Array<Validator<number>>,
): string[] {
    switch (typeof value) {
        case 'string':
            if (value.trim().length === 0) {
                return []
            } else {
                return _validate(parseFloat(value), valueValidators)
            }
        case 'number':
            if (isNaN(value)) {
                return ['Input must be a number.']
            } else {
                return validate(value, valueValidators)
            }
    }
}

interface NumberInputWrapperProps {
    label: React.ReactNode
    path: string
    prefix: React.ReactNode
    suffix: React.ReactNode
    min?: number
    max?: number
    step?: number
    defaultValue?: number
    hint: React.ReactNode
    disabled?: boolean
    persistDefaultValue?: boolean
}

const NumberInputWrapper: React.FC<NumberInputWrapperProps> = ({
    label,
    path,
    prefix,
    suffix,
    defaultValue,
    min,
    max,
    step,
    hint,
    disabled,
    persistDefaultValue = false,
}) => {
    const { doc, upsertData } = useContext(StoreContext)

    const valueValidators = useMemo<Validator<number>[]>(() => {
        return [
            input => {
                if (min && input < min) {
                    return `Input must be at least ${min}.`
                } else {
                    return undefined
                }
            },
            input => {
                if (max && input > max) {
                    return `Input must be at most ${max}.`
                } else {
                    return undefined
                }
            },
            input => {
                if (step && input % step !== 0) {
                    return `Input must be a multiple of ${step}.`
                } else {
                    return undefined
                }
            },
        ]
    }, [min, max, step])

    const storedValue = doc && get(doc.data_, path)

    useEffect(() => {
        if (
            !persistDefaultValue ||
            defaultValue === undefined ||
            !Number.isFinite(defaultValue) ||
            (storedValue !== undefined && Number(storedValue) === defaultValue)
        ) {
            return
        }

        void upsertData(
            path,
            defaultValue,
            _validate(defaultValue, valueValidators),
        )
    }, [
        defaultValue,
        path,
        persistDefaultValue,
        storedValue,
        upsertData,
        valueValidators,
    ])

    return (
        <NumberInput
            label={label}
            prefix={prefix}
            suffix={suffix}
            value={storedValue ?? defaultValue ?? ''}
            errorMessages={
                (doc &&
                    (get(
                        doc.metadata_.errors?.data_ ?? {},
                        path,
                    ) as string[])) ??
                []
            }
            min={min}
            max={max}
            step={step}
            hint={hint}
            onChange={async value =>
                await upsertData(path, value, _validate(value, valueValidators))
            }
            disabled={disabled}
        />
    )
}

export default NumberInputWrapper
